import React, { useState, useRef } from 'react';
import './HTMLEditor.css';
import LogoManager from './LogoManager';

const HTMLEditor = ({ value, onChange, placeholder = "Enter HTML content..." }) => {
  const [activeTab, setActiveTab] = useState('visual'); // 'visual' or 'html'
  const [selection, setSelection] = useState({ start: 0, end: 0 });
  const [showLogoManager, setShowLogoManager] = useState(false);
  const [selectedLogo, setSelectedLogo] = useState(null);
  const textareaRef = useRef(null);

  // Handle text formatting
  const insertTag = (openTag, closeTag = '') => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = value.substring(start, end);
    
    let newText;
    if (closeTag) {
      newText = value.substring(0, start) + openTag + selectedText + closeTag + value.substring(end);
    } else {
      newText = value.substring(0, start) + openTag + value.substring(end);
    }
    
    onChange(newText);
    
    // Set cursor position after the opening tag
    setTimeout(() => {
      const newPosition = closeTag ? start + openTag.length + selectedText.length + closeTag.length : start + openTag.length;
      textarea.setSelectionRange(newPosition, newPosition);
      textarea.focus();
    }, 10);
  };

  // Format buttons data
  const formatButtons = [
    { label: 'H1', action: () => insertTag('<h1>', '</h1>'), title: 'Heading 1' },
    { label: 'H2', action: () => insertTag('<h2>', '</h2>'), title: 'Heading 2' },
    { label: 'H3', action: () => insertTag('<h3>', '</h3>'), title: 'Heading 3' },
    { label: 'B', action: () => insertTag('<strong>', '</strong>'), title: 'Bold', style: { fontWeight: 'bold' } },
    { label: 'I', action: () => insertTag('<em>', '</em>'), title: 'Italic', style: { fontStyle: 'italic' } },
    { label: 'U', action: () => insertTag('<u>', '</u>'), title: 'Underline', style: { textDecoration: 'underline' } },
    { label: 'P', action: () => insertTag('<p>', '</p>'), title: 'Paragraph' },
    { label: 'BR', action: () => insertTag('<br/>'), title: 'Line Break' },
    { label: 'DIV', action: () => insertTag('<div>', '</div>'), title: 'Div Container' },
  ];

  // Insert template variable
  const insertTemplateVar = (variable) => {
    insertTag(`{{${variable}}}`);
  };

  // Insert logo
  const insertLogo = (logoFilename = null) => {
    if (logoFilename) {
      insertTag(`{{logo:${logoFilename}}}`);
    } else {
      insertTag('{{logo}}');
    }
  };

  // Handle logo selection
  const handleLogoSelect = (logoFilename) => {
    setSelectedLogo(logoFilename);
    if (logoFilename) {
      insertLogo(logoFilename);
    } else {
      insertLogo(); // Use default logo
    }
    setShowLogoManager(false);
  };

  const templateVariables = [
    { key: 'wellName', label: 'Well Name' },
    { key: 'reportDate', label: 'Report Date' },
    { key: 'operator', label: 'Operator' },
    { key: 'field', label: 'Field' },
    { key: 'wellType', label: 'Well Type' }
  ];

  return (
    <div className="html-editor">
      {/* Tab Headers */}
      <div className="editor-tabs">
        <button
          className={`tab-button ${activeTab === 'visual' ? 'active' : ''}`}
          onClick={() => setActiveTab('visual')}
        >
          📝 Visual
        </button>
        <button
          className={`tab-button ${activeTab === 'html' ? 'active' : ''}`}
          onClick={() => setActiveTab('html')}
        >
          🔧 HTML
        </button>
      </div>

      {activeTab === 'visual' && (
        <div className="visual-editor">
          {/* Toolbar */}
          <div className="editor-toolbar">
            <div className="toolbar-group">
              <span className="toolbar-label">Format:</span>
              {formatButtons.map((button, index) => (
                <button
                  key={index}
                  className="toolbar-btn"
                  onClick={button.action}
                  title={button.title}
                  style={button.style}
                >
                  {button.label}
                </button>
              ))}
            </div>
            
            <div className="toolbar-group">
              <span className="toolbar-label">Variables:</span>
              {templateVariables.map((variable) => (
                <button
                  key={variable.key}
                  className="toolbar-btn variable-btn"
                  onClick={() => insertTemplateVar(variable.key)}
                  title={`Insert ${variable.label}`}
                >
                  {variable.label}
                </button>
              ))}
            </div>
            
            <div className="toolbar-group">
              <span className="toolbar-label">Media:</span>
              <button
                className="toolbar-btn logo-btn"
                onClick={() => setShowLogoManager(true)}
                title="Insert Logo"
              >
                🖼️ Logo
              </button>
              <button
                className="toolbar-btn variable-btn"
                onClick={() => insertLogo()}
                title="Insert Default Logo"
              >
                {'{{logo}}'}
              </button>
            </div>
          </div>

          {/* Content Area */}
          <textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className="visual-textarea"
            rows={10}
          />
        </div>
      )}

      {activeTab === 'html' && (
        <div className="html-editor-content">
          <textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className="html-textarea"
            rows={15}
          />
        </div>
      )}
      
      {/* Logo Manager Modal */}
      {showLogoManager && (
        <LogoManager
          onLogoSelect={handleLogoSelect}
          selectedLogo={selectedLogo}
          onClose={() => setShowLogoManager(false)}
        />
      )}
    </div>
  );
};

export default HTMLEditor; 